import { requestApiJson } from './client';
import { ApiStatusError } from './templates';
import type { components } from './types.gen';
import { normalizeServiceAiArtifactPreview, type ServiceAiArtifactPreview } from './serviceAiArtifactPreview';

type JsonRecord = Record<string, unknown>;

export type ServiceAiReference = {
  id: string;
  messageId: string;
  kind: 'service' | 'marketing' | 'external' | 'conversation' | 'unknown';
  title: string;
  url: string;
  excerpt: string;
  locator: string;
  metadata: JsonRecord;
};

export type ServiceAiArtifact = {
  id: string;
  sessionId: string;
  sessionTitle: string;
  type: string;
  title: string;
  status: string;
  summary: string;
  bodyMarkdown: string;
  preview: ServiceAiArtifactPreview | null;
  accessRedacted?: boolean;
  reviewPath: string;
  version: number | null;
  updatedAt: string;
};

export type ServiceAiActionPreview = {
  id: string;
  type: string;
  toolName: string;
  title: string;
  summary: string;
  status: string;
  payload: JsonRecord;
  payloadHash: string;
  expiresAt: string;
  reviewPath: string;
  reviewState: JsonRecord;
  fields: Array<{ label: string; value: string }>;
  missingFields: string[];
};

export type ServiceAiMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  kind?: string;
  accessRedacted?: boolean;
  content: string;
  createdAt: string;
  status: string;
  images: Array<{ id: string; url: string; name: string }>;
  attachments: ServiceAiAttachment[];
  references: ServiceAiReference[];
  artifacts: ServiceAiArtifact[];
  actionPreviews: ServiceAiActionPreview[];
};

export type ServiceAiGoalSummary = {
  id: string;
  type: string;
  title: string;
  description: string;
  status: string;
  completionState: string;
  objectiveProfileId: string;
  confirmed: string[];
  openQuestions: string[];
};

export type ServiceAiSession = {
  id: string;
  title: string;
  status: string;
  preview: string;
  startedAt: string;
  updatedAt: string;
  expiresAt: string;
  entryContext: ServiceAiResolvedSessionContext | null;
  currentGoal: ServiceAiGoalSummary | null;
  answerGoalProfileId: string;
  answerGoalProfileName: string;
  task: string;
  objective: string;
  decisions: string[];
  openQuestions: string[];
};

export type ServiceAiGoalProfile = {
  id: string;
  key: string;
  name: string;
  description: string;
  taskTypes: string[];
  objective: string;
  priorities: string[];
  successCriteria: string[];
  constraints: string[];
  constraintsConfig: JsonRecord;
  priorityWeights: JsonRecord;
  tradeoffs: string[];
  avoid: string[];
  deepDive: JsonRecord;
  researchDepth: string;
  enabled: boolean;
  isDefault: boolean;
  isSystem: boolean;
  version: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type ServiceAiSettings = {
  canTune: boolean;
  usdJpyRate: number;
  model: string;
  modelSource: string;
  serviceHelpPolicy: string;
  externalResearchPolicy: string;
  sessionRetentionDays: number;
  taskDefinitions: ServiceAiTaskDefinition[];
  objectiveProfiles: ServiceAiGoalProfile[];
  updatedAt: string;
  modelSettings: ServiceAiFeatureModelSetting[];
  modelCatalog: ServiceAiModelCatalogItem[];
};

export type ServiceAiFeedbackRating = 'helpful' | 'unhelpful';
export type ServiceAiFeedbackCategory =
  | 'wrong_intent'
  | 'incorrect'
  | 'missing_context'
  | 'could_not_complete'
  | 'unsafe'
  | 'other';

export type ServiceAiConversationReviewSummary = {
  id: string;
  title: string;
  status: string;
  adminUserId: number;
  adminName: string;
  preview: string;
  messageCount: number;
  failedCount: number;
  clarificationCount: number;
  latestFeedback: null | {
    rating: ServiceAiFeedbackRating;
    category: ServiceAiFeedbackCategory;
    comment: string;
    updatedAt: string;
  };
  lastActivityAt: string;
  createdAt: string;
};

export type ServiceAiConversationReview = {
  session: JsonRecord;
  messages: JsonRecord[];
  goals: JsonRecord[];
  outcomes: JsonRecord[];
  feedback: JsonRecord[];
};

export type ServiceAiFeatureModelSetting = {
  key: string;
  name: string;
  description: string;
  model: string;
  reasoningMode: 'standard' | 'pro';
  reasoningEffort: string;
  verbosity: string;
  supportsProMode: boolean;
  source: string;
  version: number;
  usageEstimate: {
    inputTokens: string;
    outputTokens: string;
    inputTokensMin: number;
    inputTokensMax: number;
    outputTokensTypical: number;
    outputTokensMax: number;
    webSearchCallsEstimate: number;
    note: string;
  };
  updatedAt: string;
};

export type ServiceAiModelCatalogItem = {
  id: string;
  name: string;
  summary: string;
  quality: string;
  costLevel: number;
  selectionGroup: 'recommended' | 'advanced';
  supportsProMode: boolean;
  pricingUsdPerMillion: { input: number; cachedInput: number; cacheWrite: number; output: number };
  supportedReasoningEfforts: string[];
  supportedVerbosity: string[];
};

export type ServiceAiCapabilityGuide = {
  category: string;
  examplePrompt: string;
  canDo: string[];
  related: string[];
  boundary: string;
  permissionNote: string;
};

export type ServiceAiTaskDefinition = {
  id: string;
  key: string;
  name: string;
  description: string;
  goalType: string;
  instructions: string;
  goalSchema: JsonRecord;
  allowedTools: string[];
  successCriteria: string[];
  constraints: string[];
  externalResearchMode: 'never' | 'auto' | 'prefer';
  isActive: boolean;
  isSystem: boolean;
  version: number;
  sortOrder: number;
  capabilityGuide: ServiceAiCapabilityGuide | null;
};

export type ServiceAiCatalog = {
  goalProfiles: ServiceAiGoalProfile[];
  taskDefinitions: ServiceAiTaskDefinition[];
};

export type ServiceAiUploadedImage = {
  id: string;
  fileName: string;
  contentType: string;
  width: number | null;
  height: number | null;
  previewUrl: string;
};

export type ServiceAiAttachment = {
  id: string;
  fileName: string;
  contentType: string;
  kind: string;
  sizeBytes: number;
  contentUrl: string;
  status: string;
  expiresAt: string;
  metadata: JsonRecord;
  reused?: boolean;
};

export type TimetableImportCandidate = {
  eventUuid: string;
  name: string;
  eventDate: string | null;
  venue: string | null;
  score: number;
  confidence: string;
  conflicts: string[];
};

export type TimetableImportDraft = {
  id: string;
  sessionId: string | null;
  status: string;
  version: number;
  payloadHash: string;
  timetable: {
    title: string;
    targetDate: string | null;
    note: string;
    data: JsonRecord;
  };
  eventHints: { names: string[]; dates: string[]; venues: string[] };
  eventCandidates: TimetableImportCandidate[];
  selectedEvent: { eventUuid: string; name: string } | null;
  boundEventUuid: string | null;
  selectedEventUuid: string | null;
  rowCount: number;
  columnCount: number;
  confidence: number;
  warnings: Array<{ code: string; message: string; rowIndex: number | null }>;
  appliedTimetableId: number | null;
  expiresAt: string | null;
};

export type ServiceAiSessionDetail = {
  session: ServiceAiSession;
  messages: ServiceAiMessage[];
  goals: ServiceAiGoalSummary[];
  artifacts: ServiceAiArtifact[];
  actionPreviews: ServiceAiActionPreview[];
  references: ServiceAiReference[];
  nextMessageCursor: string;
  routing: {
    mode: string;
    externalResearchUsed: boolean;
    externalResearchUnavailableReason: string;
  } | null;
  timetableImportDraft: TimetableImportDraft | null;
};

export type ServiceAiSessionContext = {
  kind: 'event_marketing';
  eventUuid: string;
};

export type ServiceAiResolvedSessionContext = ServiceAiSessionContext & {
  eventName: string;
};

const isRecord = (value: unknown): value is JsonRecord =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const asRecord = (value: unknown): JsonRecord => (isRecord(value) ? value : {});
const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const asString = (value: unknown): string => (value == null ? '' : String(value));
const asBoolean = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : value == null ? fallback : ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase());

function firstValue(record: JsonRecord, keys: string[]): unknown {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) return record[key];
  }
  return undefined;
}

function strings(value: unknown): string[] {
  return asArray(value).map(asString).map((item) => item.trim()).filter(Boolean);
}

function summaryText(value: unknown): string {
  if (!isRecord(value)) return asString(value);
  return asString(firstValue(value, ['conversationSummary', 'conversation_summary', 'summary', 'title', 'name', 'description']));
}

function normalizeReference(value: unknown, index: number): ServiceAiReference {
  const item = asRecord(value);
  const sourceType = asString(firstValue(item, ['kind', 'sourceType', 'source_type', 'type'])).toLowerCase();
  const metadata = asRecord(firstValue(item, ['metadata', 'meta']));
  const referenceKind = asString(firstValue(metadata, ['referenceKind', 'reference_kind'])).toLowerCase();
  let kind: ServiceAiReference['kind'] = 'unknown';
  if (sourceType.includes('external') || sourceType.includes('web')) kind = 'external';
  else if (referenceKind === 'marketing_knowledge') kind = 'marketing';
  else if (sourceType.includes('service') || sourceType.includes('internal') || sourceType.includes('notebook')) kind = 'service';
  else if (sourceType.includes('conversation')) kind = 'conversation';
  return {
    id: asString(firstValue(item, ['id', 'referenceId', 'reference_id'])) || `reference-${index}`,
    messageId: asString(firstValue(item, ['messageId', 'message_id'])),
    kind,
    title: asString(firstValue(item, ['title', 'label', 'name']))
      || (kind === 'external' ? '外部情報' : kind === 'marketing' ? 'マーケティング知識' : 'サービス内情報'),
    url: asString(firstValue(item, ['url', 'href', 'sourceUrl', 'source_url'])),
    excerpt: asString(firstValue(item, ['excerpt', 'snippet', 'summary', 'content'])),
    locator: asString(firstValue(item, ['locator', 'sourceLocator', 'source_locator'])),
    metadata,
  };
}

function normalizeArtifact(value: unknown, index: number): ServiceAiArtifact {
  const item = asRecord(value);
  const currentVersion = asRecord(firstValue(item, ['currentVersion', 'current_version']));
  const versionContent = asRecord(firstValue(currentVersion, ['content']));
  const accessRedacted = firstValue(item, ['accessRedacted', 'access_redacted']) === true;
  const versionRaw = Number(
    firstValue(item, ['version', 'versionNumber', 'version_number']) ?? firstValue(currentVersion, ['version']),
  );
  return {
    id: asString(firstValue(item, ['id', 'artifactId', 'artifact_id'])) || `artifact-${index}`,
    sessionId: asString(firstValue(item, ['sessionId', 'session_id'])),
    sessionTitle: accessRedacted ? '' : asString(firstValue(item, ['sessionTitle', 'session_title'])),
    type: asString(firstValue(item, ['type', 'artifactType', 'artifact_type'])) || 'document',
    title: accessRedacted ? '内容を確認できない成果物' : asString(firstValue(item, ['title', 'name'])) || '作成中の成果物',
    status: asString(firstValue(item, ['status', 'state'])) || 'draft',
    summary: accessRedacted ? '' :
      asString(firstValue(item, ['summary', 'description', 'body'])) ||
      asString(firstValue(currentVersion, ['changeSummary', 'change_summary'])),
    bodyMarkdown: accessRedacted ? '' : asString(firstValue(currentVersion, ['bodyMarkdown', 'body_markdown'])),
    preview: accessRedacted ? null : normalizeServiceAiArtifactPreview(versionContent.preview),
    accessRedacted,
    reviewPath: asString(firstValue(item, ['reviewPath', 'review_path', 'path', 'url'])),
    version: Number.isFinite(versionRaw) && versionRaw > 0 ? versionRaw : null,
    updatedAt: asString(firstValue(item, ['updatedAt', 'updated_at', 'createdAt', 'created_at'])),
  };
}

function normalizeActionFields(value: unknown): Array<{ label: string; value: string }> {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        const item = asRecord(entry);
        return {
          label: asString(firstValue(item, ['label', 'name', 'key'])),
          value: asString(firstValue(item, ['value', 'text', 'displayValue', 'display_value'])),
        };
      })
      .filter((entry) => entry.label || entry.value);
  }
  if (isRecord(value)) {
    return Object.entries(value).map(([label, fieldValue]) => ({ label, value: asString(fieldValue) }));
  }
  return [];
}

function normalizeMarketingActionFields(value: unknown): Array<{ label: string; value: string }> {
  const payload = asRecord(value);
  const overview = asRecord(payload.overview);
  const initiatives = asArray(payload.initiatives).map(asRecord);
  const fields: Array<{ label: string; value: string }> = [];
  [
    ['目的', overview.objective],
    ['対象', overview.audience],
    ['中心メッセージ', overview.keyMessage],
  ].forEach(([label, fieldValue]) => {
    const normalized = asString(fieldValue).trim().slice(0, 240);
    if (normalized) fields.push({ label: String(label), value: normalized });
  });
  const plannedBudget = Number(overview.plannedBudget);
  if (
    overview.plannedBudget !== null
    && overview.plannedBudget !== undefined
    && overview.plannedBudget !== ''
    && Number.isFinite(plannedBudget)
    && plannedBudget >= 0
  ) {
    fields.push({ label: '計画予算', value: `${plannedBudget.toLocaleString('ja-JP')}円` });
  }
  if (initiatives.length) {
    const names = initiatives
      .map((initiative) => asString(initiative.title).trim())
      .filter(Boolean)
      .slice(0, 3);
    fields.push({
      label: '提案施策',
      value: `${initiatives.length}件${names.length ? `（${names.join('、')}）` : ''}`,
    });
  }
  return fields;
}

function normalizeActionPreview(value: unknown, index: number): ServiceAiActionPreview {
  const item = asRecord(value);
  const navigationTarget = asRecord(firstValue(item, ['navigationTarget', 'navigation_target']));
  const payload = firstValue(item, ['payload', 'fields', 'previewFields', 'preview_fields']);
  const displayFields = firstValue(item, ['fields', 'previewFields', 'preview_fields']);
  const explicitToolName = asString(firstValue(item, ['toolName', 'tool_name']));
  const actionType = asString(firstValue(item, ['type', 'actionType', 'action_type']));
  const toolName = explicitToolName || actionType;
  return {
    id: asString(firstValue(item, ['id', 'actionId', 'action_id'])) || `action-${index}`,
    type: actionType || explicitToolName || 'draft',
    toolName,
    title: asString(firstValue(item, ['title', 'name'])) || '下書きを作成しました',
    summary: asString(firstValue(item, ['summary', 'description'])),
    status: asString(firstValue(item, ['status', 'state'])) || 'draft',
    payload: asRecord(payload),
    payloadHash: asString(firstValue(item, ['payloadHash', 'payload_hash'])),
    expiresAt: asString(firstValue(item, ['expiresAt', 'expires_at'])),
    reviewPath:
      asString(firstValue(item, ['reviewPath', 'review_path', 'path', 'url'])) ||
      asString(firstValue(navigationTarget, ['path'])),
    reviewState: asRecord(firstValue(navigationTarget, ['state'])),
    fields: displayFields !== undefined
      ? normalizeActionFields(displayFields)
      : toolName === 'open_event_ticket_setup'
        ? []
        : toolName === 'open_event_marketing_plan_review'
          ? normalizeMarketingActionFields(payload)
          : normalizeActionFields(payload),
    missingFields: strings(firstValue(item, ['missingFields', 'missing_fields', 'openQuestions', 'open_questions'])),
  };
}

export function normalizeServiceAiMessage(value: unknown, index = 0): ServiceAiMessage {
  const item = asRecord(value);
  const payload = asRecord(item.payload);
  const metadata = asRecord(item.metadata);
  const content = firstValue(item, ['content', 'text', 'message']) ?? firstValue(payload, ['content', 'text', 'message']);
  const rawRole = asString(firstValue(item, ['role', 'senderRole', 'sender_role'])).toLowerCase();
  const role: ServiceAiMessage['role'] = rawRole === 'user' ? 'user' : rawRole === 'system' ? 'system' : 'assistant';
  const imageItems = asArray(firstValue(item, ['images', 'uploadedImages', 'uploaded_images']) ?? payload.images);
  const attachmentItems = asArray(firstValue(item, ['attachments', 'uploadedAttachments', 'uploaded_attachments']) ?? payload.attachments);
  const referenceItems = asArray(firstValue(item, ['references', 'citations']) ?? firstValue(payload, ['references', 'citations']));
  const artifactItems = asArray(firstValue(item, ['artifacts']) ?? payload.artifacts);
  const actionItems = asArray(firstValue(item, ['actionPreviews', 'action_previews', 'actions']) ?? firstValue(payload, ['actionPreviews', 'action_previews', 'actions']));
  return {
    id: asString(firstValue(item, ['id', 'messageId', 'message_id'])) || `message-${index}`,
    role,
    kind: asString(item.kind),
    accessRedacted: item.accessRedacted === true || metadata.accessRedacted === true,
    content: asString(content),
    createdAt: asString(firstValue(item, ['createdAt', 'created_at', 'timestamp'])),
    status: asString(firstValue(item, ['status', 'state'])) || 'complete',
    images: imageItems.map((entry, imageIndex) => {
      const image = asRecord(entry);
      return {
        id: asString(firstValue(image, ['id', 'imageId', 'image_id'])) || `image-${imageIndex}`,
        url: asString(firstValue(image, ['url', 'src', 'imageUrl', 'image_url'])),
        name: asString(firstValue(image, ['name', 'fileName', 'file_name'])) || '投稿画像',
      };
    }),
    attachments: attachmentItems.map((entry, attachmentIndex) => normalizeServiceAiAttachment(entry, attachmentIndex)),
    references: referenceItems.map(normalizeReference),
    artifacts: artifactItems.map(normalizeArtifact),
    actionPreviews: actionItems.map(normalizeActionPreview),
  };
}

function normalizeGoalSummary(value: unknown): ServiceAiGoalSummary | null {
  if (!isRecord(value)) return null;
  const fieldValues = asArray(firstValue(value, ['fieldValues', 'field_values']));
  const confirmedFromFields = fieldValues
    .map(asRecord)
    .filter((field) => ['confirmed', 'approved'].includes(asString(field.state).toLowerCase()))
    .map((field) => {
      const key = asString(firstValue(field, ['label', 'key']));
      const fieldValue = asString(firstValue(field, ['value']));
      return [key, fieldValue].filter(Boolean).join(': ');
    })
    .filter(Boolean);
  const openFromFields = fieldValues
    .map(asRecord)
    .filter((field) => ['missing', 'partial', 'hypothesis', 'needs_confirmation'].includes(asString(field.state).toLowerCase()))
    .map((field) => asString(firstValue(field, ['label', 'key'])))
    .filter(Boolean);
  return {
    id: asString(firstValue(value, ['id', 'goalId', 'goal_id'])),
    type: asString(firstValue(value, ['type', 'goalType', 'goal_type'])) || 'free_chat',
    title: asString(firstValue(value, ['title', 'name'])) || '会話から整理中',
    description: asString(firstValue(value, ['description', 'summary'])),
    status: asString(firstValue(value, ['status', 'state'])) || 'forming',
    completionState: asString(firstValue(value, ['completionState', 'completion_state'])),
    objectiveProfileId: asString(firstValue(value, ['objectiveProfileId', 'objective_profile_id'])),
    confirmed: [
      ...strings(firstValue(value, ['confirmed', 'confirmedItems', 'confirmed_items', 'decisions'])),
      ...confirmedFromFields,
    ],
    openQuestions: [
      ...strings(firstValue(value, ['openQuestions', 'open_questions', 'unresolved'])),
      ...openFromFields,
    ],
  };
}

export function normalizeServiceAiSession(value: unknown): ServiceAiSession {
  const item = asRecord(value);
  const goal = firstValue(item, ['currentGoal', 'current_goal', 'goal']);
  const answerGoal = asRecord(firstValue(item, ['answerGoalProfile', 'answer_goal_profile']));
  const rawSummary = firstValue(item, ['summary']);
  const summary = asRecord(rawSummary);
  const entryContext = asRecord(firstValue(item, ['entryContext', 'entry_context']));
  const task = summaryText(firstValue(summary, [
    'task',
    'taskDefinition',
    'task_definition',
    'currentTask',
    'current_task',
  ]));
  const objective = summaryText(firstValue(summary, [
    'objective',
    'objectiveProfile',
    'objective_profile',
    'currentObjective',
    'current_objective',
  ]));
  const summaryDecisions = strings(firstValue(summary, ['decisions', 'confirmedItems', 'confirmed_items']));
  const summaryOpenQuestions = strings(firstValue(summary, ['openQuestions', 'open_questions', 'unresolved']));
  return {
    id: asString(firstValue(item, ['id', 'sessionId', 'session_id'])),
    title: asString(firstValue(item, ['title', 'name'])) || '新しい会話',
    status: asString(firstValue(item, ['status', 'state'])) || 'active',
    preview:
      asString(firstValue(item, ['preview', 'lastMessagePreview', 'last_message_preview'])) ||
      summaryText(firstValue(summary, ['conversationSummary', 'conversation_summary'])) ||
      (!isRecord(rawSummary) ? asString(rawSummary) : '') ||
      objective ||
      task,
    startedAt: asString(firstValue(item, ['startedAt', 'started_at', 'createdAt', 'created_at'])),
    updatedAt: asString(firstValue(item, ['updatedAt', 'updated_at', 'lastActivityAt', 'last_activity_at'])),
    expiresAt: asString(firstValue(item, ['expiresAt', 'expires_at'])),
    entryContext:
      asString(firstValue(entryContext, ['kind'])) === 'event_marketing'
      && asString(firstValue(entryContext, ['eventUuid', 'event_uuid']))
        ? {
            kind: 'event_marketing',
            eventUuid: asString(firstValue(entryContext, ['eventUuid', 'event_uuid'])),
            eventName: asString(firstValue(entryContext, ['eventName', 'event_name'])),
          }
        : null,
    currentGoal: normalizeGoalSummary(goal),
    answerGoalProfileId:
      asString(firstValue(item, ['answerGoalProfileId', 'answer_goal_profile_id'])) ||
      asString(firstValue(answerGoal, ['id'])),
    answerGoalProfileName:
      asString(firstValue(item, ['answerGoalProfileName', 'answer_goal_profile_name'])) ||
      asString(firstValue(answerGoal, ['name', 'title'])),
    task,
    objective,
    decisions: strings(firstValue(item, ['decisions', 'confirmedItems', 'confirmed_items'])).length
      ? strings(firstValue(item, ['decisions', 'confirmedItems', 'confirmed_items']))
      : summaryDecisions,
    openQuestions: strings(firstValue(item, ['openQuestions', 'open_questions', 'unresolved'])).length
      ? strings(firstValue(item, ['openQuestions', 'open_questions', 'unresolved']))
      : summaryOpenQuestions,
  };
}

export function normalizeServiceAiGoalProfile(value: unknown, index = 0): ServiceAiGoalProfile {
  const item = asRecord(value);
  const sortOrder = Number(firstValue(item, ['sortOrder', 'sort_order', 'position']));
  const rawObjective = asString(firstValue(item, ['objective', 'goal', 'instruction', 'instructions']));
  const instructionLines = rawObjective.split(/\r?\n/);
  const priorityLineIndex = instructionLines.findIndex((line) => line.trim().startsWith('優先順位:'));
  const prioritiesFromInstructions = priorityLineIndex >= 0
    ? instructionLines[priorityLineIndex]
        .replace(/^\s*優先順位:\s*/, '')
        .split(/[、,]/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    : [];
  return {
    id: asString(firstValue(item, ['id', 'profileId', 'profile_id'])) || `profile-${index}`,
    key: asString(firstValue(item, ['key', 'profileKey', 'profile_key'])),
    name: asString(firstValue(item, ['name', 'title'])) || '名称未設定',
    description: asString(firstValue(item, ['description', 'summary'])),
    taskTypes: strings(firstValue(item, ['taskTypes', 'task_types', 'appliesTo', 'applies_to'])),
    objective: priorityLineIndex >= 0
      ? instructionLines.filter((_, lineIndex) => lineIndex !== priorityLineIndex).join('\n').trim()
      : rawObjective,
    priorities:
      strings(firstValue(item, ['priorities', 'priorityOrder', 'priority_order'])).length > 0
        ? strings(firstValue(item, ['priorities', 'priorityOrder', 'priority_order']))
        : prioritiesFromInstructions,
    successCriteria: strings(firstValue(item, ['successCriteria', 'success_criteria'])),
    constraints: strings(firstValue(item, ['constraints', 'nonGoals', 'non_goals'])),
    constraintsConfig: asRecord(firstValue(item, ['constraintsConfig', 'constraints_config'])),
    priorityWeights: asRecord(firstValue(item, ['priorityWeights', 'priority_weights'])),
    tradeoffs: strings(firstValue(item, ['tradeoffs'])),
    avoid: strings(firstValue(item, ['avoid'])),
    deepDive: asRecord(firstValue(item, ['deepDive', 'deep_dive'])),
    researchDepth: asString(firstValue(item, ['researchDepth', 'research_depth'])) || 'balanced',
    enabled: asBoolean(firstValue(item, ['enabled', 'isEnabled', 'is_enabled', 'isActive', 'is_active']), true),
    isDefault: asBoolean(firstValue(item, ['isDefault', 'is_default']), false),
    isSystem: asBoolean(firstValue(item, ['isSystem', 'is_system']), false),
    version: Math.max(1, Number(firstValue(item, ['version'])) || 1),
    sortOrder: Number.isFinite(sortOrder) ? sortOrder : index,
    createdAt: asString(firstValue(item, ['createdAt', 'created_at'])),
    updatedAt: asString(firstValue(item, ['updatedAt', 'updated_at'])),
  };
}

export function normalizeServiceAiTaskDefinition(value: unknown, index = 0): ServiceAiTaskDefinition {
  const item = asRecord(value);
  const capabilityGuide = asRecord(firstValue(item, ['capabilityGuide', 'capability_guide']));
  const sortOrder = Number(firstValue(item, ['sortOrder', 'sort_order', 'position']));
  const rawExternalMode = asString(
    firstValue(item, ['externalResearchMode', 'external_research_mode']),
  ).toLowerCase();
  const externalResearchMode: ServiceAiTaskDefinition['externalResearchMode'] =
    rawExternalMode === 'never' || rawExternalMode === 'prefer' ? rawExternalMode : 'auto';
  return {
    id: asString(firstValue(item, ['id', 'definitionId', 'definition_id'])) || `task-definition-${index}`,
    key: asString(firstValue(item, ['key', 'slug', 'goalType', 'goal_type'])) || `task-${index}`,
    name: asString(firstValue(item, ['name', 'title'])) || '名称未設定',
    description: asString(firstValue(item, ['description', 'summary'])),
    goalType: asString(firstValue(item, ['goalType', 'goal_type'])) || 'free_chat',
    instructions: asString(firstValue(item, ['instructions', 'instruction', 'objective'])),
    goalSchema: asRecord(firstValue(item, ['goalSchema', 'goal_schema'])),
    allowedTools: strings(firstValue(item, ['allowedTools', 'allowed_tools'])),
    successCriteria: strings(firstValue(item, ['successCriteria', 'success_criteria'])),
    constraints: strings(firstValue(item, ['constraints', 'nonGoals', 'non_goals'])),
    externalResearchMode,
    isActive: asBoolean(firstValue(item, ['isActive', 'is_active', 'enabled']), true),
    isSystem: asBoolean(firstValue(item, ['isSystem', 'is_system']), false),
    version: Math.max(1, Number(firstValue(item, ['version'])) || 1),
    sortOrder: Number.isFinite(sortOrder) ? sortOrder : index,
    capabilityGuide: Object.keys(capabilityGuide).length
      ? {
          category: asString(firstValue(capabilityGuide, ['category'])),
          examplePrompt: asString(firstValue(capabilityGuide, ['examplePrompt', 'example_prompt'])),
          canDo: strings(firstValue(capabilityGuide, ['canDo', 'can_do'])),
          related: strings(firstValue(capabilityGuide, ['related'])),
          boundary: asString(firstValue(capabilityGuide, ['boundary'])),
          permissionNote: asString(firstValue(capabilityGuide, ['permissionNote', 'permission_note'])),
        }
      : null,
  };
}

function normalizeSessionDetailPayload(value: unknown): ServiceAiSessionDetail {
  const raw = payloadRecord(value);
  const rawGoals = asArray(firstValue(raw, ['goals']));
  const singularGoal = firstValue(raw, ['goal']);
  if (!rawGoals.length && singularGoal) rawGoals.push(singularGoal);
  const goals = rawGoals.map((goal) => normalizeGoalSummary(goal)).filter(Boolean) as ServiceAiGoalSummary[];
  const artifacts = asArray(firstValue(raw, ['artifacts'])).map(normalizeArtifact);
  const actionPreviews = asArray(firstValue(raw, ['actions', 'actionPreviews', 'action_previews'])).map(normalizeActionPreview);
  const references = asArray(firstValue(raw, ['references', 'citations'])).map(normalizeReference);
  const messages = asArray(firstValue(raw, ['messages'])).map(normalizeServiceAiMessage);
  messages.forEach((message, index) => {
    if (message.references.length) return;
    const messageReferences = references.filter((reference) => reference.messageId === message.id);
    if (messageReferences.length) messages[index] = { ...message, references: messageReferences };
  });
  const assistantIndex = messages.map((message) => message.role).lastIndexOf('assistant');
  if (assistantIndex >= 0) {
    const unboundReferences = references.filter((reference) => !reference.messageId);
    messages[assistantIndex] = {
      ...messages[assistantIndex],
      artifacts: messages[assistantIndex].artifacts.length ? messages[assistantIndex].artifacts : artifacts,
      actionPreviews: messages[assistantIndex].actionPreviews.length
        ? messages[assistantIndex].actionPreviews
        : actionPreviews,
      references: messages[assistantIndex].references.length
        ? messages[assistantIndex].references
        : unboundReferences,
    };
  }
  const session = normalizeServiceAiSession(firstValue(raw, ['session']) ?? raw);
  const currentGoal = goals.find((goal) => goal.id && goal.id === asString(asRecord(firstValue(raw, ['session'])).currentGoalId))
    || goals.find((goal) => ['active', 'ready', 'forming'].includes(goal.status))
    || goals[0]
    || session.currentGoal;
  return {
    session: { ...session, currentGoal: currentGoal || null },
    messages,
    goals,
    artifacts,
    actionPreviews,
    references,
    nextMessageCursor: asString(firstValue(raw, ['nextMessageCursor', 'next_message_cursor'])),
    routing: isRecord(raw.routing)
      ? {
          mode: asString(firstValue(raw.routing, ['mode'])),
          externalResearchUsed: asBoolean(
            firstValue(raw.routing, ['externalResearchUsed', 'external_research_used']),
          ),
          externalResearchUnavailableReason: asString(
            firstValue(raw.routing, [
              'externalResearchUnavailableReason',
              'external_research_unavailable_reason',
            ]),
          ),
        }
      : null,
    timetableImportDraft: isRecord(firstValue(raw, ['timetableImportDraft', 'timetable_import_draft']))
      ? normalizeTimetableImportDraft(firstValue(raw, ['timetableImportDraft', 'timetable_import_draft']))
      : null,
  };
}

function payloadRecord(value: unknown): JsonRecord {
  const root = asRecord(value);
  return isRecord(root.data) ? root.data : root;
}

async function ensurePayload<T>(
  result: Awaited<ReturnType<typeof requestApiJson<T>>>,
  fallbackMessage: string,
): Promise<T> {
  if (result.response.ok && result.data != null) return result.data;
  const payload = asRecord(result.data ?? result.error);
  const error = new ApiStatusError(result.response.status) as ApiStatusError & { data?: JsonRecord; code?: string };
  error.message = asString(firstValue(payload, ['message', 'error'])) || fallbackMessage;
  error.code = asString(firstValue(payload, ['code', 'errorCode', 'error_code'])) || undefined;
  error.data = payload;
  throw error;
}

const sessionPath = (sessionId: string): string =>
  `/admin/service-ai/sessions/${encodeURIComponent(String(sessionId || '').trim())}`;

export function getServiceAiArtifactDocxUrl(sessionId: string, artifactId: string): string {
  const origin = String(process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  return `${origin}/api${sessionPath(sessionId)}/artifacts/${encodeURIComponent(
    String(artifactId || '').trim(),
  )}/export.docx`;
}

export function getServiceAiOwnedArtifactDocxUrl(artifactId: string): string {
  const origin = String(process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  return `${origin}/api/admin/service-ai/artifacts/${encodeURIComponent(
    String(artifactId || '').trim(),
  )}/export.docx`;
}

export async function listServiceAiArtifacts(input: {
  cursor?: string;
  limit?: number;
  signal?: AbortSignal;
} = {}): Promise<{ artifacts: ServiceAiArtifact[]; nextCursor: string }> {
  const result = await requestApiJson('/admin/service-ai/artifacts', {
    query: { cursor: input.cursor || '', limit: String(input.limit || 50) },
    signal: input.signal,
  });
  const raw = payloadRecord(await ensurePayload(result, '成果物一覧の取得に失敗しました'));
  return {
    artifacts: asArray(firstValue(raw, ['artifacts', 'items'])).map(normalizeArtifact),
    nextCursor: asString(firstValue(raw, ['nextCursor', 'next_cursor'])),
  };
}

export async function deleteServiceAiArtifact(
  artifactId: string,
  expectedVersion: number,
): Promise<string> {
  const result = await requestApiJson(
    `/admin/service-ai/artifacts/${encodeURIComponent(String(artifactId || '').trim())}`,
    { method: 'DELETE', body: { expectedVersion } },
  );
  const raw = payloadRecord(await ensurePayload(result, '成果物の削除に失敗しました'));
  return asString(firstValue(raw, ['deletedArtifactId', 'deleted_artifact_id'])) || artifactId;
}

export async function listServiceAiSessions(
  query: { status?: string; limit?: number; cursor?: string } = {},
  options: { signal?: AbortSignal } = {},
): Promise<{ sessions: ServiceAiSession[]; nextCursor: string }> {
  const result = await requestApiJson('/admin/service-ai/sessions', {
    query: { status: query.status || 'all', limit: query.limit || 30, cursor: query.cursor || '' },
    signal: options.signal,
  });
  const raw = payloadRecord(await ensurePayload(result, 'AI会話一覧の取得に失敗しました'));
  return {
    sessions: asArray(firstValue(raw, ['sessions', 'items'])).map(normalizeServiceAiSession),
    nextCursor: asString(firstValue(raw, ['nextCursor', 'next_cursor', 'cursor'])),
  };
}

export async function getServiceAiSession(
  sessionId: string,
  options: { signal?: AbortSignal; messageLimit?: number; messageCursor?: string } = {},
): Promise<ServiceAiSessionDetail> {
  const result = await requestApiJson(sessionPath(sessionId), {
    query: {
      messageLimit: options.messageLimit || 50,
      messageCursor: options.messageCursor || '',
    },
    signal: options.signal,
  });
  return normalizeSessionDetailPayload(
    await ensurePayload(result, 'AI会話の取得に失敗しました'),
  );
}

export async function createServiceAiSession(
  input: {
    title?: string;
    goalType?: string;
    objectiveProfileId?: string;
    context?: ServiceAiSessionContext;
  } = {},
): Promise<ServiceAiSessionDetail> {
  const result = await requestApiJson('/admin/service-ai/sessions', {
    method: 'POST',
    body: {
      title: input.title || undefined,
      goalType: input.goalType || undefined,
      objectiveProfileId: input.objectiveProfileId || undefined,
      context: input.context?.kind && input.context?.eventUuid
        ? { kind: input.context.kind, eventUuid: input.context.eventUuid }
        : undefined,
    },
  });
  const raw = payloadRecord(await ensurePayload(result, 'AI会話の開始に失敗しました'));
  const goal = normalizeGoalSummary(firstValue(raw, ['goal']));
  return {
    session: {
      ...normalizeServiceAiSession(firstValue(raw, ['session']) ?? raw),
      currentGoal: goal,
    },
    messages: [],
    goals: goal ? [goal] : [],
    artifacts: [],
    actionPreviews: [],
    references: [],
    nextMessageCursor: '',
    routing: null,
    timetableImportDraft: null,
  };
}

export async function sendServiceAiMessage(
  sessionId: string,
  input: {
    clientMessageId: string;
    content: string;
    imageIds?: string[];
    attachmentIds?: string[];
    boundEventUuid?: string;
    goalType?: string;
    objectiveProfileId?: string;
    artifactRevision?: { artifactId: string; expectedVersion: number };
    externalResearch?: 'auto' | 'off' | 'on';
    platform?: 'web' | 'mobile' | 'macos';
  },
): Promise<ServiceAiSessionDetail> {
  const attachmentIds = input.attachmentIds?.filter(Boolean) || [];
  if (attachmentIds.length > 3 || new Set(attachmentIds).size !== attachmentIds.length) {
    throw new Error('添付ファイルは重複なしで3件まで指定してください');
  }
  const result = await requestApiJson(`${sessionPath(sessionId)}/messages`, {
    method: 'POST',
    body: {
      clientMessageId: input.clientMessageId,
      content: input.content,
      imageIds: input.imageIds?.length ? input.imageIds : undefined,
      attachmentIds: attachmentIds.length ? attachmentIds : undefined,
      boundEventUuid: input.boundEventUuid || undefined,
      goalType: input.goalType || undefined,
      objectiveProfileId: input.objectiveProfileId || undefined,
      artifactRevision: input.artifactRevision?.artifactId
        ? {
            artifactId: input.artifactRevision.artifactId,
            expectedVersion: input.artifactRevision.expectedVersion,
          }
        : undefined,
      externalResearch: input.externalResearch || 'auto',
      platform: input.platform || 'web',
    },
  });
  const raw = payloadRecord(await ensurePayload(result, 'メッセージの送信に失敗しました'));
  const messages = [
    firstValue(raw, ['userMessage', 'user_message']),
    firstValue(raw, ['assistantMessage', 'assistant_message', 'message']),
  ].filter(Boolean);
  return normalizeSessionDetailPayload({ ...raw, messages });
}

export async function uploadServiceAiImages(
  sessionId: string,
  files: File[],
): Promise<ServiceAiUploadedImage[]> {
  const normalized = Array.isArray(files) ? files.filter(Boolean).slice(0, 5) : [];
  if (!normalized.length) return [];
  const form = new FormData();
  normalized.forEach((file) => form.append('images', file, file.name));
  const result = await requestApiJson(`${sessionPath(sessionId)}/images`, {
    method: 'POST',
    body: form,
  });
  const raw = payloadRecord(await ensurePayload(result, '画像のアップロードに失敗しました'));
  return asArray(firstValue(raw, ['images', 'items'])).map((value, index) => {
    const item = asRecord(value);
    const width = Number(firstValue(item, ['width']));
    const height = Number(firstValue(item, ['height']));
    return {
      id: asString(firstValue(item, ['id', 'imageId', 'image_id'])) || `uploaded-image-${index}`,
      fileName: asString(firstValue(item, ['fileName', 'file_name', 'name'])) || normalized[index]?.name || '画像',
      contentType: asString(firstValue(item, ['contentType', 'content_type', 'type'])),
      width: Number.isFinite(width) && width > 0 ? width : null,
      height: Number.isFinite(height) && height > 0 ? height : null,
      previewUrl: asString(firstValue(item, ['previewUrl', 'preview_url', 'url'])),
    };
  });
}

function normalizeServiceAiAttachment(value: unknown, index = 0): ServiceAiAttachment {
  const item = asRecord(value);
  return {
    id: asString(firstValue(item, ['id', 'attachmentId', 'attachment_id'])) || `attachment-${index}`,
    fileName: asString(firstValue(item, ['fileName', 'file_name', 'name'])) || '添付ファイル',
    contentType: asString(firstValue(item, ['contentType', 'content_type', 'type'])),
    kind: asString(firstValue(item, ['kind'])) || 'document',
    sizeBytes: Math.max(0, Number(firstValue(item, ['sizeBytes', 'size_bytes'])) || 0),
    contentUrl: asString(firstValue(item, ['contentUrl', 'content_url', 'url'])),
    status: asString(firstValue(item, ['status'])) || 'available',
    expiresAt: asString(firstValue(item, ['expiresAt', 'expires_at'])),
    metadata: asRecord(firstValue(item, ['metadata'])),
    reused: asBoolean(firstValue(item, ['reused'])),
  };
}

export async function uploadServiceAiAttachments(
  sessionId: string,
  files: File[],
): Promise<ServiceAiAttachment[]> {
  const normalized = Array.isArray(files) ? files.filter(Boolean) : [];
  if (normalized.length > 3) {
    throw new Error('一度に添付できるファイルは3件までです');
  }
  if (!normalized.length) return [];
  const form = new FormData();
  normalized.forEach((file) => form.append('attachments', file, file.name));
  const result = await requestApiJson(`${sessionPath(sessionId)}/attachments`, {
    method: 'POST',
    body: form,
  });
  const raw = payloadRecord(await ensurePayload(result, '添付ファイルのアップロードに失敗しました'));
  return asArray(firstValue(raw, ['attachments', 'items'])).map(normalizeServiceAiAttachment);
}

function normalizeTimetableImportCandidate(value: unknown): TimetableImportCandidate {
  const item = asRecord(value);
  return {
    eventUuid: asString(firstValue(item, ['eventUuid', 'event_uuid'])),
    name: asString(firstValue(item, ['name', 'title'])),
    eventDate: asString(firstValue(item, ['eventDate', 'event_date'])) || null,
    venue: asString(firstValue(item, ['venue', 'venueLabel', 'venue_label'])) || null,
    score: Number(firstValue(item, ['score'])) || 0,
    confidence: asString(firstValue(item, ['confidence'])) || 'low',
    conflicts: strings(firstValue(item, ['conflicts'])),
  };
}

function normalizeTimetableImportDraft(value: unknown): TimetableImportDraft {
  const item = asRecord(value);
  const timetable = asRecord(firstValue(item, ['timetable']));
  const hints = asRecord(firstValue(item, ['eventHints', 'event_hints']));
  const selectedEvent = asRecord(firstValue(item, ['selectedEvent', 'selected_event']));
  const appliedId = Number(firstValue(item, ['appliedTimetableId', 'applied_timetable_id']));
  return {
    id: asString(firstValue(item, ['id', 'draftId', 'draft_id'])),
    sessionId: asString(firstValue(item, ['sessionId', 'session_id'])) || null,
    status: asString(firstValue(item, ['status'])) || 'review',
    version: Math.max(1, Number(firstValue(item, ['version'])) || 1),
    payloadHash: asString(firstValue(item, ['payloadHash', 'payload_hash'])),
    timetable: {
      title: asString(firstValue(timetable, ['title'])) || 'AI取込タイムテーブル',
      targetDate: asString(firstValue(timetable, ['targetDate', 'target_date'])) || null,
      note: asString(firstValue(timetable, ['note'])),
      data: asRecord(firstValue(timetable, ['data'])),
    },
    eventHints: {
      names: strings(firstValue(hints, ['names'])),
      dates: strings(firstValue(hints, ['dates'])),
      venues: strings(firstValue(hints, ['venues'])),
    },
    eventCandidates: asArray(firstValue(item, ['eventCandidates', 'event_candidates'])).map(normalizeTimetableImportCandidate),
    selectedEvent: selectedEvent.eventUuid || selectedEvent.event_uuid
      ? { eventUuid: asString(firstValue(selectedEvent, ['eventUuid', 'event_uuid'])), name: asString(firstValue(selectedEvent, ['name'])) }
      : null,
    boundEventUuid: asString(firstValue(item, ['boundEventUuid', 'bound_event_uuid'])) || null,
    selectedEventUuid: asString(firstValue(item, ['selectedEventUuid', 'selected_event_uuid'])) || null,
    rowCount: Math.max(0, Number(firstValue(item, ['rowCount', 'row_count'])) || 0),
    columnCount: Math.max(0, Number(firstValue(item, ['columnCount', 'column_count'])) || 0),
    confidence: Math.max(0, Math.min(1, Number(firstValue(item, ['confidence'])) || 0)),
    warnings: asArray(firstValue(item, ['warnings'])).map((warning) => {
      const entry = asRecord(warning);
      const rowIndex = Number(firstValue(entry, ['rowIndex', 'row_index']));
      return {
        code: asString(firstValue(entry, ['code'])),
        message: asString(firstValue(entry, ['message'])),
        rowIndex: Number.isInteger(rowIndex) && rowIndex >= 0 ? rowIndex : null,
      };
    }),
    appliedTimetableId: Number.isInteger(appliedId) && appliedId > 0 ? appliedId : null,
    expiresAt: asString(firstValue(item, ['expiresAt', 'expires_at'])) || null,
  };
}

export async function getTimetableImportDraft(
  draftId: string,
  eventUuid?: string,
): Promise<{ draft: TimetableImportDraft; attachments: ServiceAiAttachment[] }> {
  const result = await requestApiJson(`/admin/service-ai/timetable-imports/${encodeURIComponent(draftId)}`, {
    query: { eventUuid: eventUuid || '' },
  });
  const raw = payloadRecord(await ensurePayload(result, 'タイムテーブル取込案の取得に失敗しました'));
  return {
    draft: normalizeTimetableImportDraft(firstValue(raw, ['draft'])),
    attachments: asArray(firstValue(raw, ['attachments'])).map(normalizeServiceAiAttachment),
  };
}

export async function applyTimetableImportDraft(input: {
  draftId: string;
  eventUuid: string;
  version: number;
  payloadHash: string;
  title?: string;
  targetDate?: string | null;
  note?: string;
}): Promise<{ draft: TimetableImportDraft; timetable: JsonRecord; created: boolean }> {
  const result = await requestApiJson(
    `/admin/service-ai/timetable-imports/${encodeURIComponent(input.draftId)}/apply`,
    {
      method: 'POST',
      body: {
        eventUuid: input.eventUuid,
        version: input.version,
        payloadHash: input.payloadHash,
        title: input.title,
        targetDate: input.targetDate,
        note: input.note,
      },
    },
  );
  const raw = payloadRecord(await ensurePayload(result, 'タイムテーブルの反映に失敗しました'));
  return {
    draft: normalizeTimetableImportDraft(firstValue(raw, ['draft'])),
    timetable: asRecord(firstValue(raw, ['timetable'])),
    created: asBoolean(firstValue(raw, ['created'])),
  };
}

export async function getServiceAiCatalog(
  options: { includeDisabled?: boolean; signal?: AbortSignal } = {},
): Promise<ServiceAiCatalog> {
  const result = await requestApiJson('/admin/service-ai/goal-profiles', {
    query: { includeDisabled: options.includeDisabled ? '1' : '' },
    signal: options.signal,
  });
  const raw = payloadRecord(await ensurePayload(result, '回答ゴールの取得に失敗しました'));
  return {
    goalProfiles: asArray(firstValue(raw, ['goalProfiles', 'goal_profiles', 'profiles', 'items']))
      .map(normalizeServiceAiGoalProfile)
      .sort((left, right) => left.sortOrder - right.sortOrder),
    taskDefinitions: asArray(firstValue(raw, ['taskDefinitions', 'task_definitions']))
      .map(normalizeServiceAiTaskDefinition)
      .sort((left, right) => left.sortOrder - right.sortOrder),
  };
}

export async function listServiceAiGoalProfiles(
  options: { includeDisabled?: boolean; signal?: AbortSignal } = {},
): Promise<ServiceAiGoalProfile[]> {
  return (await getServiceAiCatalog(options)).goalProfiles;
}

export type ServiceAiGoalProfileInput = Omit<
  ServiceAiGoalProfile,
  | 'id' | 'key' | 'isSystem' | 'version' | 'createdAt' | 'updatedAt'
  | 'constraintsConfig' | 'priorityWeights' | 'tradeoffs' | 'avoid' | 'deepDive' | 'researchDepth'
> & Partial<Pick<
  ServiceAiGoalProfile,
  'constraintsConfig' | 'priorityWeights' | 'tradeoffs' | 'avoid' | 'deepDive' | 'researchDepth'
>> & { version?: number };

function goalProfileBody(input: ServiceAiGoalProfileInput): JsonRecord {
  return {
    name: input.name,
    description: input.description,
    taskTypes: input.taskTypes,
    instructions: [input.objective, input.priorities.length ? `優先順位: ${input.priorities.join('、')}` : '']
      .filter(Boolean)
      .join('\n'),
    priorities: input.priorities,
    successCriteria: input.successCriteria,
    constraints: { ...(input.constraintsConfig || {}), items: input.constraints },
    priorityWeights: input.priorityWeights,
    tradeoffs: input.tradeoffs,
    avoid: input.avoid,
    deepDive: input.deepDive,
    researchDepth: input.researchDepth,
    isActive: input.enabled,
    sortOrder: input.sortOrder,
  };
}

export async function createServiceAiGoalProfile(
  input: ServiceAiGoalProfileInput,
): Promise<ServiceAiGoalProfile> {
  const result = await requestApiJson('/admin/service-ai/settings/objective-profiles', {
    method: 'POST',
    body: goalProfileBody(input),
  });
  const raw = payloadRecord(await ensurePayload(result, '回答ゴールの作成に失敗しました'));
  return normalizeServiceAiGoalProfile(
    firstValue(raw, ['objectiveProfile', 'objective_profile', 'goalProfile', 'goal_profile', 'profile']) ?? raw,
  );
}

export async function updateServiceAiGoalProfile(
  profileId: string,
  input: ServiceAiGoalProfileInput,
): Promise<ServiceAiGoalProfile> {
  const result = await requestApiJson(
    `/admin/service-ai/settings/objective-profiles/${encodeURIComponent(String(profileId || '').trim())}`,
    {
      method: 'PATCH',
      body: { ...goalProfileBody(input), expectedVersion: input.version },
    },
  );
  const raw = payloadRecord(await ensurePayload(result, '回答ゴールの更新に失敗しました'));
  return normalizeServiceAiGoalProfile(
    firstValue(raw, ['objectiveProfile', 'objective_profile', 'goalProfile', 'goal_profile', 'profile']) ?? raw,
  );
}

export type ServiceAiTaskDefinitionInput = Omit<ServiceAiTaskDefinition, 'id' | 'isSystem' | 'version'> & {
  version?: number;
};

function taskDefinitionBody(input: ServiceAiTaskDefinitionInput): JsonRecord {
  return {
    key: input.key || undefined,
    name: input.name,
    description: input.description,
    goalType: input.goalType,
    instructions: input.instructions,
    goalSchema: input.goalSchema,
    allowedTools: input.allowedTools,
    successCriteria: input.successCriteria,
    constraints: input.constraints,
    externalResearchMode: input.externalResearchMode,
    isActive: input.isActive,
    sortOrder: input.sortOrder,
  };
}

export async function createServiceAiTaskDefinition(
  input: ServiceAiTaskDefinitionInput,
): Promise<ServiceAiTaskDefinition> {
  const result = await requestApiJson('/admin/service-ai/settings/task-definitions', {
    method: 'POST',
    body: taskDefinitionBody(input),
  });
  const raw = payloadRecord(await ensurePayload(result, 'タスク別AI設定の作成に失敗しました'));
  return normalizeServiceAiTaskDefinition(
    firstValue(raw, ['taskDefinition', 'task_definition', 'definition']) ?? raw,
  );
}

export async function updateServiceAiTaskDefinition(
  definitionId: string,
  input: ServiceAiTaskDefinitionInput,
): Promise<ServiceAiTaskDefinition> {
  const result = await requestApiJson(
    `/admin/service-ai/settings/task-definitions/${encodeURIComponent(String(definitionId || '').trim())}`,
    {
      method: 'PATCH',
      body: { ...taskDefinitionBody(input), expectedVersion: input.version },
    },
  );
  const raw = payloadRecord(await ensurePayload(result, 'タスク別AI設定の更新に失敗しました'));
  return normalizeServiceAiTaskDefinition(
    firstValue(raw, ['taskDefinition', 'task_definition', 'definition']) ?? raw,
  );
}

export async function updateServiceAiArtifactStatus(
  sessionId: string,
  artifactId: string,
  status: 'review' | 'adopted' | 'archived',
  expectedVersion?: number | null,
): Promise<ServiceAiArtifact> {
  const result = await requestApiJson(
    `${sessionPath(sessionId)}/artifacts/${encodeURIComponent(String(artifactId || '').trim())}`,
    {
      method: 'PATCH',
      body: {
        status,
        expectedVersion: Number.isFinite(Number(expectedVersion))
          ? Number(expectedVersion)
          : undefined,
      },
    },
  );
  const raw = payloadRecord(await ensurePayload(result, '成果物の状態更新に失敗しました'));
  return normalizeArtifact(firstValue(raw, ['artifact']) ?? raw, 0);
}

export async function createServiceAiArtifactVersion(
  sessionId: string,
  artifactId: string,
  input: {
    bodyMarkdown: string;
    content?: JsonRecord;
    changeSummary?: string;
    expectedVersion: number;
  },
): Promise<ServiceAiArtifact> {
  const result = await requestApiJson(
    `${sessionPath(sessionId)}/artifacts/${encodeURIComponent(String(artifactId || '').trim())}/versions`,
    {
      method: 'POST',
      body: {
        bodyMarkdown: input.bodyMarkdown,
        content: input.content,
        changeSummary: input.changeSummary || undefined,
        expectedVersion: input.expectedVersion,
      },
    },
  );
  const raw = payloadRecord(await ensurePayload(result, '成果物の新しい版を保存できませんでした'));
  return normalizeArtifact(firstValue(raw, ['artifact']) ?? raw, 0);
}

export async function reviewServiceAiActionPreview(
  sessionId: string,
  actionId: string,
  status: 'approved' | 'rejected',
  payloadHash: string,
): Promise<ServiceAiActionPreview> {
  const result = await requestApiJson(
    `${sessionPath(sessionId)}/action-previews/${encodeURIComponent(String(actionId || '').trim())}`,
    { method: 'PATCH', body: { status, payloadHash } },
  );
  const raw = payloadRecord(await ensurePayload(result, '確認案の更新に失敗しました'));
  return normalizeActionPreview(
    firstValue(raw, ['actionPreview', 'action_preview', 'action']) ?? raw,
    0,
  );
}

export async function getServiceAiSettings(
  options: { signal?: AbortSignal } = {},
): Promise<ServiceAiSettings> {
  const result = await requestApiJson('/admin/service-ai/settings', { signal: options.signal });
  const raw = payloadRecord(await ensurePayload(result, 'AI設定の取得に失敗しました'));
  const settings = asRecord(firstValue(raw, ['settings']) ?? raw);
  const model = asRecord(firstValue(settings, ['model']));
  const normalizeModelSetting = (value: unknown): ServiceAiFeatureModelSetting => {
    const item = asRecord(value);
    const usage = asRecord(firstValue(item, ['usageEstimate', 'usage_estimate']));
    return {
      key: asString(firstValue(item, ['key', 'featureKey', 'feature_key'])),
      name: asString(firstValue(item, ['name', 'label'])),
      description: asString(firstValue(item, ['description'])),
      model: asString(firstValue(item, ['model'])),
      reasoningMode: asString(firstValue(item, ['reasoningMode', 'reasoning_mode'])) === 'pro'
        ? 'pro'
        : 'standard',
      reasoningEffort: asString(firstValue(item, ['reasoningEffort', 'reasoning_effort'])) || 'low',
      verbosity: asString(firstValue(item, ['verbosity'])) || 'medium',
      supportsProMode: asBoolean(firstValue(item, ['supportsProMode', 'supports_pro_mode'])),
      source: asString(firstValue(item, ['source'])) || 'default',
      version: Number(firstValue(item, ['version'])) || 1,
      usageEstimate: {
        inputTokens: asString(firstValue(usage, ['inputTokens', 'input_tokens'])),
        outputTokens: asString(firstValue(usage, ['outputTokens', 'output_tokens'])),
        inputTokensMin: Number(firstValue(usage, ['inputTokensMin', 'input_tokens_min'])) || 0,
        inputTokensMax: Number(firstValue(usage, ['inputTokensMax', 'input_tokens_max'])) || 0,
        outputTokensTypical: Number(firstValue(usage, ['outputTokensTypical', 'output_tokens_typical'])) || 0,
        outputTokensMax: Number(firstValue(usage, ['outputTokensMax', 'output_tokens_max'])) || 0,
        webSearchCallsEstimate: Number(firstValue(usage, ['webSearchCallsEstimate', 'web_search_calls_estimate'])) || 0,
        note: asString(firstValue(usage, ['note'])),
      },
      updatedAt: asString(firstValue(item, ['updatedAt', 'updated_at'])),
    };
  };
  const normalizeModelCatalogItem = (value: unknown): ServiceAiModelCatalogItem => {
    const item = asRecord(value);
    const pricing = asRecord(firstValue(item, ['pricingUsdPerMillion', 'pricing_usd_per_million']));
    return {
      id: asString(firstValue(item, ['id', 'model'])),
      name: asString(firstValue(item, ['name', 'label'])),
      summary: asString(firstValue(item, ['summary', 'description'])),
      quality: asString(firstValue(item, ['quality'])),
      costLevel: Number(firstValue(item, ['costLevel', 'cost_level'])) || 1,
      selectionGroup: asString(firstValue(item, ['selectionGroup', 'selection_group'])) === 'recommended'
        ? 'recommended'
        : 'advanced',
      supportsProMode: asBoolean(firstValue(item, ['supportsProMode', 'supports_pro_mode'])),
      pricingUsdPerMillion: {
        input: Number(firstValue(pricing, ['input'])) || 0,
        cachedInput: Number(firstValue(pricing, ['cachedInput', 'cached_input'])) || 0,
        cacheWrite: Number(firstValue(pricing, ['cacheWrite', 'cache_write'])) || 0,
        output: Number(firstValue(pricing, ['output'])) || 0,
      },
      supportedReasoningEfforts: strings(firstValue(item, ['supportedReasoningEfforts', 'supported_reasoning_efforts'])),
      supportedVerbosity: strings(firstValue(item, ['supportedVerbosity', 'supported_verbosity'])),
    };
  };
  const retention = Number(firstValue(settings, ['sessionRetentionDays', 'session_retention_days']));
  return {
    canTune: asBoolean(firstValue(settings, ['canTune', 'can_tune']), false),
    usdJpyRate: Number(firstValue(settings, ['usdJpyRate', 'usd_jpy_rate'])) || 160,
    model:
      asString(firstValue(model, ['model', 'name'])) ||
      asString(firstValue(settings, ['modelName', 'model_name'])),
    modelSource: asString(firstValue(model, ['source'])) || 'default',
    serviceHelpPolicy: asString(firstValue(settings, ['serviceHelpPolicy', 'service_help_policy'])) || 'サービス内資料を優先し、外部検索を使わない',
    externalResearchPolicy: asString(firstValue(settings, ['externalResearchPolicy', 'external_research_policy'])) || '一般・エンタメ業界の調査では必要に応じて外部情報を利用する',
    sessionRetentionDays: Number.isFinite(retention) && retention > 0 ? retention : 30,
    taskDefinitions: asArray(firstValue(settings, ['taskDefinitions', 'task_definitions']))
      .map(normalizeServiceAiTaskDefinition)
      .sort((left, right) => left.sortOrder - right.sortOrder),
    objectiveProfiles: asArray(firstValue(settings, ['objectiveProfiles', 'objective_profiles']))
      .map(normalizeServiceAiGoalProfile)
      .sort((left, right) => left.sortOrder - right.sortOrder),
    modelSettings: asArray(firstValue(settings, ['modelSettings', 'model_settings'])).map(normalizeModelSetting),
    modelCatalog: asArray(firstValue(settings, ['modelCatalog', 'model_catalog'])).map(normalizeModelCatalogItem),
    updatedAt: asString(firstValue(settings, ['updatedAt', 'updated_at'])),
  };
}

export async function submitServiceAiFeedback(
  sessionId: string,
  input: {
    messageId?: string;
    rating: ServiceAiFeedbackRating;
    category?: ServiceAiFeedbackCategory;
    comment?: string;
  },
): Promise<JsonRecord> {
  const result = await requestApiJson(`${sessionPath(sessionId)}/feedback`, {
    method: 'POST',
    body: input,
  });
  const raw = payloadRecord(await ensurePayload(result, 'フィードバックを保存できませんでした'));
  return asRecord(firstValue(raw, ['feedback']) ?? raw);
}

export async function listServiceAiConversationReviews(input: {
  limit?: number;
  cursor?: string;
  outcomeStatus?: string;
  rating?: string;
  adminUserId?: number;
} = {}): Promise<{ conversations: ServiceAiConversationReviewSummary[]; nextCursor: string }> {
  const params = new URLSearchParams();
  if (input.limit) params.set('limit', String(input.limit));
  if (input.cursor) params.set('cursor', input.cursor);
  if (input.outcomeStatus) params.set('outcomeStatus', input.outcomeStatus);
  if (input.rating) params.set('rating', input.rating);
  if (input.adminUserId) params.set('adminUserId', String(input.adminUserId));
  const query = params.toString();
  const result = await requestApiJson(
    `/admin/service-ai/settings/conversation-reviews${query ? `?${query}` : ''}`,
  );
  const raw = payloadRecord(await ensurePayload(result, 'AI会話レビュー一覧を取得できませんでした'));
  return {
    conversations: asArray(firstValue(raw, ['conversations'])).map((value) => {
      const item = asRecord(value);
      const feedback = asRecord(firstValue(item, ['latestFeedback', 'latest_feedback']));
      const rating = asString(firstValue(feedback, ['rating'])) as ServiceAiFeedbackRating;
      return {
        id: asString(firstValue(item, ['id'])),
        title: asString(firstValue(item, ['title'])) || '新しい会話',
        status: asString(firstValue(item, ['status'])),
        adminUserId: Number(firstValue(item, ['adminUserId', 'admin_user_id'])) || 0,
        adminName: asString(firstValue(item, ['adminName', 'admin_name'])) || '管理者',
        preview: asString(firstValue(item, ['preview'])),
        messageCount: Number(firstValue(item, ['messageCount', 'message_count'])) || 0,
        failedCount: Number(firstValue(item, ['failedCount', 'failed_count'])) || 0,
        clarificationCount: Number(firstValue(item, ['clarificationCount', 'clarification_count'])) || 0,
        latestFeedback: rating ? {
          rating,
          category: (asString(firstValue(feedback, ['category'])) || 'other') as ServiceAiFeedbackCategory,
          comment: asString(firstValue(feedback, ['comment'])),
          updatedAt: asString(firstValue(feedback, ['updatedAt', 'updated_at'])),
        } : null,
        lastActivityAt: asString(firstValue(item, ['lastActivityAt', 'last_activity_at'])),
        createdAt: asString(firstValue(item, ['createdAt', 'created_at'])),
      };
    }),
    nextCursor: asString(firstValue(raw, ['nextCursor', 'next_cursor'])),
  };
}

export async function getServiceAiConversationReview(
  sessionId: string,
): Promise<ServiceAiConversationReview> {
  const result = await requestApiJson(
    `/admin/service-ai/settings/conversation-reviews/${encodeURIComponent(sessionId)}`,
  );
  const raw = payloadRecord(await ensurePayload(result, 'AI会話レビューを取得できませんでした'));
  const review = asRecord(firstValue(raw, ['review']) ?? raw);
  return {
    session: asRecord(firstValue(review, ['session'])),
    messages: asArray(firstValue(review, ['messages'])).map(asRecord),
    goals: asArray(firstValue(review, ['goals'])).map(asRecord),
    outcomes: asArray(firstValue(review, ['outcomes'])).map(asRecord),
    feedback: asArray(firstValue(review, ['feedback'])).map(asRecord),
  };
}

export async function updateServiceAiFeatureModelSetting(
  featureKey: string,
  input: Pick<ServiceAiFeatureModelSetting, 'model' | 'reasoningMode' | 'reasoningEffort' | 'verbosity' | 'version'>,
): Promise<ServiceAiFeatureModelSetting> {
  const result = await requestApiJson(
    `/admin/service-ai/settings/models/${encodeURIComponent(featureKey)}`,
    {
      method: 'PATCH',
      body: {
        model: input.model,
        reasoningMode: input.reasoningMode,
        reasoningEffort: input.reasoningEffort,
        verbosity: input.verbosity,
        expectedVersion: input.version,
      },
    },
  );
  const raw = payloadRecord(await ensurePayload(result, 'AI機能のモデル設定を保存できませんでした'));
  const setting = asRecord(firstValue(raw, ['setting']) ?? raw);
  const usage = asRecord(firstValue(setting, ['usageEstimate', 'usage_estimate']));
  return {
    key: asString(firstValue(setting, ['key', 'featureKey', 'feature_key'])),
    name: asString(firstValue(setting, ['name', 'label'])),
    description: asString(firstValue(setting, ['description'])),
    model: asString(firstValue(setting, ['model'])),
    reasoningMode: asString(firstValue(setting, ['reasoningMode', 'reasoning_mode'])) === 'pro'
      ? 'pro'
      : 'standard',
    reasoningEffort: asString(firstValue(setting, ['reasoningEffort', 'reasoning_effort'])),
    verbosity: asString(firstValue(setting, ['verbosity'])),
    supportsProMode: asBoolean(firstValue(setting, ['supportsProMode', 'supports_pro_mode'])),
    source: asString(firstValue(setting, ['source'])),
    version: Number(firstValue(setting, ['version'])) || 1,
    usageEstimate: {
      inputTokens: asString(firstValue(usage, ['inputTokens', 'input_tokens'])),
      outputTokens: asString(firstValue(usage, ['outputTokens', 'output_tokens'])),
      inputTokensMin: Number(firstValue(usage, ['inputTokensMin', 'input_tokens_min'])) || 0,
      inputTokensMax: Number(firstValue(usage, ['inputTokensMax', 'input_tokens_max'])) || 0,
      outputTokensTypical: Number(firstValue(usage, ['outputTokensTypical', 'output_tokens_typical'])) || 0,
      outputTokensMax: Number(firstValue(usage, ['outputTokensMax', 'output_tokens_max'])) || 0,
      webSearchCallsEstimate: Number(firstValue(usage, ['webSearchCallsEstimate', 'web_search_calls_estimate'])) || 0,
      note: asString(firstValue(usage, ['note'])),
    },
    updatedAt: asString(firstValue(setting, ['updatedAt', 'updated_at'])),
  };
}

export type AiBudgetPolicy = {
  id: string;
  scopeType: 'organization' | 'user';
  adminUserId: number | null;
  enabled: boolean;
  dailyTokenLimit: number | null;
  monthlyTokenLimit: number | null;
  dailyBudgetJpy: number | null;
  monthlyBudgetJpy: number | null;
  dailyGraceTokens: number;
  dailyGraceTurns: number;
  alertThresholds: number[];
  usdJpyRate: number | null;
  version: number;
};

export type AiUsageAggregate = {
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  totalTokens: number;
  webSearchCalls: number;
  estimatedCostUsd: number;
  estimatedCostJpy: number;
  requestCount: number;
};

export type AiUsageSummary = {
  today: AiUsageAggregate;
  month: AiUsageAggregate;
  byFeature?: {
    serviceAi: { today: AiUsageAggregate; month: AiUsageAggregate };
    chatFollowup: { today: AiUsageAggregate; month: AiUsageAggregate };
    surveys: { today: AiUsageAggregate; month: AiUsageAggregate };
  };
  featureCosts?: Array<{
    key: string;
    name: string;
    requestCount: number;
    totalTokens: number;
    webSearchCalls: number;
    estimatedCostJpy: number;
    perRequestCostJpy: { p50: number; p90: number; max: number };
    perRequestTokens: { p50: number; p90: number; max: number };
  }>;
  forecast?: {
    method: 'current_month_daily_run_rate';
    elapsedDays: number;
    daysInMonth: number;
    projectedMonthCostJpy: number;
    monthlyBudgetJpy: number | null;
    remainingBudgetJpy: number | null;
    budgetUsedPercent: number | null;
    projectedBudgetPercent: number | null;
  };
  organizationPolicy: AiBudgetPolicy | null;
  userPolicy: AiBudgetPolicy | null;
  effectivePolicy: AiBudgetPolicy | null;
  alerts: JsonRecord[];
  pricing?: JsonRecord;
  usdJpyRate?: number;
};

export type AiUsageLogItem = components['schemas']['AiUsageLogItem'];
export type AiUsageLogActorType = components['schemas']['AiUsageLogActorType'];
export type AiUsageLogTriggerType = components['schemas']['AiUsageLogTriggerType'];
export type AiUsageLogFeatureKey = components['schemas']['ServiceAiFeatureKey'];
export type AiUsageLogListPage = Omit<components['schemas']['AiUsageLogListResponse'], 'success'>;

export async function updateServiceAiSession(
  sessionId: string,
  patch: { title?: string; status?: 'active' | 'archived' },
): Promise<ServiceAiSession> {
  const body: Record<string, unknown> = {};
  if (patch.title !== undefined) body.title = patch.title;
  if (patch.status !== undefined) body.status = patch.status;
  const result = await requestApiJson(sessionPath(sessionId), { method: 'PATCH', body });
  const raw = payloadRecord(await ensurePayload(result, 'AI会話の更新に失敗しました'));
  return normalizeServiceAiSession(firstValue(raw, ['session']) ?? raw);
}

export async function deleteServiceAiSession(sessionId: string): Promise<{
  deletedSessionId: string;
  preservedArtifactCount: number;
}> {
  const result = await requestApiJson(sessionPath(sessionId), { method: 'DELETE' });
  const raw = payloadRecord(await ensurePayload(result, '会話履歴を削除できませんでした'));
  return {
    deletedSessionId: asString(firstValue(raw, ['deletedSessionId', 'deleted_session_id'])),
    preservedArtifactCount: Number(firstValue(raw, ['preservedArtifactCount', 'preserved_artifact_count']) || 0),
  };
}

export async function getServiceAiUsage(options: { signal?: AbortSignal } = {}): Promise<AiUsageSummary> {
  const result = await requestApiJson('/admin/service-ai/usage', { signal: options.signal });
  const raw = payloadRecord(await ensurePayload(result, 'AI利用量を取得できませんでした'));
  return asRecord(firstValue(raw, ['usage'])) as AiUsageSummary;
}

export async function getServiceAiAdminUsage(options: {
  signal?: AbortSignal;
  cursor?: string;
  limit?: number;
} = {}): Promise<JsonRecord> {
  const result = await requestApiJson('/admin/service-ai/settings/usage', {
    signal: options.signal,
    query: { cursor: options.cursor || '', limit: options.limit || 50 },
  });
  return payloadRecord(await ensurePayload(result, 'AI利用状況を取得できませんでした'));
}

export async function listServiceAiUsageLogs(
  query: {
    from?: string;
    to?: string;
    actorType?: AiUsageLogActorType | '';
    actorQuery?: string;
    featureKey?: AiUsageLogFeatureKey | '';
    triggerType?: AiUsageLogTriggerType | '';
    limit?: number;
    cursor?: string;
  } = {},
  options: { signal?: AbortSignal } = {},
): Promise<AiUsageLogListPage> {
  const result = await requestApiJson('/admin/service-ai/settings/usage-logs', {
    signal: options.signal,
    query: {
      from: query.from || '',
      to: query.to || '',
      actorType: query.actorType || '',
      actorQuery: query.actorQuery || '',
      featureKey: query.featureKey || '',
      triggerType: query.triggerType || '',
      limit: query.limit || 50,
      cursor: query.cursor || '',
    },
  });
  const raw = payloadRecord(await ensurePayload(result, 'AIトークン利用ログを取得できませんでした'));
  return {
    generatedAt: asString(firstValue(raw, ['generatedAt', 'generated_at'])),
    timezone: 'Asia/Tokyo',
    period: asRecord(firstValue(raw, ['period'])) as AiUsageLogListPage['period'],
    items: asArray(firstValue(raw, ['items'])) as AiUsageLogItem[],
    nextCursor: asString(firstValue(raw, ['nextCursor', 'next_cursor'])) || null,
  };
}

export async function getServiceAiUsageLog(
  usageEventId: string,
  options: { signal?: AbortSignal } = {},
): Promise<AiUsageLogItem> {
  const result = await requestApiJson(
    `/admin/service-ai/settings/usage-logs/${encodeURIComponent(String(usageEventId || '').trim())}`,
    { signal: options.signal },
  );
  const raw = payloadRecord(await ensurePayload(result, 'AIトークン利用ログを取得できませんでした'));
  return asRecord(firstValue(raw, ['item'])) as AiUsageLogItem;
}

type AiBudgetPolicyBaseInput = Omit<AiBudgetPolicy, 'id' | 'scopeType' | 'adminUserId' | 'usdJpyRate' | 'version'> & {
  expectedVersion: number;
};
export type OrganizationAiBudgetPolicyInput = AiBudgetPolicyBaseInput & { usdJpyRate?: number | null };
export type UserAiBudgetPolicyInput = AiBudgetPolicyBaseInput;

async function updateAiBudgetPolicy(
  path: string,
  input: OrganizationAiBudgetPolicyInput | UserAiBudgetPolicyInput,
): Promise<AiBudgetPolicy> {
  const result = await requestApiJson(path, { method: 'PUT', body: input });
  const raw = payloadRecord(await ensurePayload(result, 'AI利用予算を保存できませんでした'));
  return asRecord(firstValue(raw, ['policy'])) as AiBudgetPolicy;
}

export function updateOrganizationAiBudget(input: OrganizationAiBudgetPolicyInput): Promise<AiBudgetPolicy> {
  return updateAiBudgetPolicy('/admin/service-ai/settings/budgets/organization', input);
}

export function updateUserAiBudget(adminUserId: number, input: UserAiBudgetPolicyInput): Promise<AiBudgetPolicy> {
  return updateAiBudgetPolicy(`/admin/service-ai/settings/budgets/users/${adminUserId}`, input);
}
